import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import ImageListModal from '../../../../../../modules/components/modal/ImageListModal';
import { useToast } from '../../../../../../modules/components/toast/ToastContext';
import {
  CategorizeServiceSection,
  FieldStatus,
  SiService,
  siteIntelligenceServiceLabel,
} from '../../../../mappings';
import UrlLabelThumbnailCard from '../../card/UrlLabelThumbnailCard';
import SingleAccordion from '../../../../../../modules/components/accordion/SingleAccordion';
import BrandTable from './BrandTable';
import { AddBrandsModal } from '../../modal/AddBrandsModal';
import { HistoryModal } from '../../modal/HistoryModal';
import { getImagePathByFilename } from '../../utils';
import { useListCategorizeAndBrandImages } from '../../hooks/useListCategorizeAndBrandImages';
import EditableCardContainer from '../../card/EditableCardContainer';
import type { Request, UsePageDataManagerResult } from '../../hooks/usePageDataManager';
import ToggleLabelCard from '../../card/ToggleLabelCard';
import { getFieldUpdate, SiRequestUser } from '../../utils';
import { SiAiLinksModal } from '../../modal/SiAiLinksModal';

interface Props {
  data: {
    [SiService.BRAND_DETECT_SERVICE]: any;
    [SiService.CATEGORIZE_SERVICE]: any;
  };
  isSiAiNewData?: boolean;
  isCarry?: boolean;
  carryRequestId?: string;
  reviewer?: SiRequestUser[];
  status: string;
  refetch: () => void;
  isVisible: boolean;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSaveMultiple?: UsePageDataManagerResult['saveMultipleServices'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
  isPublic?: boolean;
}

export interface BrandImage {
  imageUrl: string;
  filePath: string;
  brandNames?: string[];
}

export default function Detail({
  data,
  isSiAiNewData,
  isCarry,
  carryRequestId,
  reviewer,
  status,
  refetch,
  isVisible,
  isEditable,
  onUpdate,
  onSaveMultiple,
  onReset,
  checkDirty,
  isPublic = false,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_BRAND_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const emDash = '—';
  const shouldRenderSiAiReadonlyStyle = !isEditable && !!isSiAiNewData;

  const [userOverrides, setUserOverrides] = useState(new Map<string, Set<string>>());
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [showAddBrandsModal, setShowAddBrandsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBrandLinksModal, setShowBrandLinksModal] = useState(false);
  const [selectedBrandLinks, setSelectedBrandLinks] =
    useState<{ brand: string; urls: string[] }>(null);
  const [editingUrlKey, setEditingUrlKey] = useState<string | null>(null);
  const [editingUrlValue, setEditingUrlValue] = useState('');
  const [selectedBrandData, setSelectedBrandData] =
    useState<{ brand: string; images: BrandImage[] }>(null);
  const brandFieldUpdates = getFieldUpdate(
    'result.brands',
    data?.[SiService.CATEGORIZE_SERVICE]?.field_updates
  );
  const hasHistory = brandFieldUpdates.length > 0;

  const brandResultsAvailable =
    data?.[SiService.BRAND_DETECT_SERVICE]?.result?.summary?.by_brand?.length > 0;
  const categoryResultsAvailable = data?.[SiService.CATEGORIZE_SERVICE]?.result?.brands?.length > 0;
  const hasBrands = brandResultsAvailable || categoryResultsAvailable;
  const categorizeBrandsDetailIndex = data?.[SiService.CATEGORIZE_SERVICE]?.detail?.findIndex(
    (d) => d?.section === CategorizeServiceSection.BRAND
  );

  const {
    data: imageData,
    status: imagesStatus,
    refetch: refetchImages,
  } = useListCategorizeAndBrandImages(
    isVisible,
    isCarry && carryRequestId ? [carryRequestId, params?.id] : params?.id,
    isPublic
  );

  const processed = useMemo(() => {
    type BrandObject = {
      isHidden: boolean;
      sector?: string;
      snippets?: string[];
      imageUrls?: string[];
    };
    type BrandMapType = Map<
      string, // brand
      BrandObject
    >;
    type UrlBrandMapType = Map<string, BrandMapType>;

    const urlBrandMap: UrlBrandMapType = new Map();
    const savedHiddenBrands: typeof userOverrides = new Map();
    const urlToImageMap = new Map<string, BrandImage[]>();
    const dirtyServiceMap = new Map<SiService, boolean>();

    if (!isVisible || !hasBrands) {
      return { urlBrandMap, savedHiddenBrands, urlToImageMap, dirtyServiceMap };
    }

    if (checkDirty) {
      dirtyServiceMap
        .set(SiService.BRAND_DETECT_SERVICE, checkDirty(SiService.BRAND_DETECT_SERVICE))
        .set(
          SiService.CATEGORIZE_SERVICE,
          data?.[SiService.CATEGORIZE_SERVICE] &&
          checkDirty(SiService.CATEGORIZE_SERVICE, [
            `detail[${categorizeBrandsDetailIndex}]`,
            'result.brands',
          ])
        );
    }

    // Process Images
    const imageResults = imageData?.results?.data ?? [];
    const imageUrlSet = new Set<string>();
    imageResults.forEach((item: any) => {
      const hasImageUrl = item?.image_url && imageUrlSet.has(item.image_url);
      const url = item?.url;
      if (!urlToImageMap.has(url)) {
        urlToImageMap.set(url, []);
      }
      if (!hasImageUrl) {
        if (item?.image_url) imageUrlSet.add(item?.image_url);
        urlToImageMap.get(url).push({
          imageUrl: item?.image_url,
          filePath: getImagePathByFilename(item?.file_path),
          brandNames: item?.brand ? [item?.brand] : [],
        });
      } else if (hasImageUrl && item?.brand) {
        const index = urlToImageMap.get(url).findIndex((img) => img.imageUrl === item.image_url);
        urlToImageMap.get(url)[index].brandNames.push(item?.brand);
      }
    });

    // Merge logic for Brands
    const mergeBrands = (url: string, newBrands: BrandMapType) => {
      if (!urlBrandMap.has(url)) {
        urlBrandMap.set(url, new Map());
      }
      const innerMap = urlBrandMap.get(url);

      newBrands.forEach((value, brandName) => {
        const existing = innerMap?.get(brandName) ?? {};
        innerMap.set(brandName, { ...existing, ...value });

        if (value.isHidden) {
          if (!savedHiddenBrands.has(url)) {
            savedHiddenBrands.set(url, new Set());
          }
          savedHiddenBrands.get(url).add(brandName);
        }
      });
    };

    // Get brands from Categorize Service
    const categorizeBrands =
      data?.[SiService.CATEGORIZE_SERVICE]?.detail?.[categorizeBrandsDetailIndex]?.section_detail ??
      [];
    categorizeBrands?.forEach((item: any) => {
      const url = item?.url;
      const map: BrandMapType = new Map();
      item?.brands?.forEach((b: any) => {
        map.set(b?.name, {
          sector: b?.sector,
          snippets: b?.snippets ?? [],
          isHidden: b?.field_status !== FieldStatus.ACTIVE,
        });
      });
      if (map.size > 0) {
        mergeBrands(url, map);
      }
    });

    // Get brands from Brand Detect Service
    data?.[SiService.BRAND_DETECT_SERVICE]?.result?.urls_certified_brands?.forEach((item: any) => {
      const url = item?.url;
      const map: BrandMapType = new Map();
      item?.certified_brands?.forEach((b: any) => {
        map.set(b?.brand_name, {
          imageUrls: b?.image_urls ?? [],
          isHidden: false, // TODO: get from API
        });
      });
      if (map.size > 0) {
        mergeBrands(url, map);
      }
    });

    return { urlBrandMap, savedHiddenBrands, urlToImageMap, dirtyServiceMap };
  }, [imageData, data, isVisible, hasBrands]);

  // TODO: update to include brand detect check
  const isDirty =
    userOverrides.size > 0 || Array.from(processed.dirtyServiceMap.values()).some((v) => v);

  const activeHiddenBrands = useMemo(() => {
    const merged: typeof userOverrides = new Map();
    const allUrls = new Set([
      ...Array.from(processed.savedHiddenBrands.keys()),
      ...Array.from(userOverrides.keys()),
    ]);

    allUrls.forEach((url) => {
      const userSet = userOverrides.get(url) || new Set();
      const apiSet = processed.savedHiddenBrands.get(url) || new Set();
      const finalSet = new Set(apiSet);

      userSet.forEach((brand) => {
        if (finalSet.has(brand)) {
          finalSet.delete(brand); // User toggled an API-hidden brand -> Show it
        } else {
          finalSet.add(brand); // User toggled an API-shown brand -> Hide it
        }
      });

      merged.set(url, finalSet);
    });

    return merged;
  }, [processed.savedHiddenBrands, userOverrides]);

  const hasVisibleUrlBrandMap = useMemo(() => {
    return Array.from(processed.urlBrandMap).some(([url, obj]) => {
      const hiddenBrands = activeHiddenBrands.get(url);
      return Array.from(obj.keys()).some((brandName) => !hiddenBrands?.has(brandName));
    });
  }, [processed.urlBrandMap, activeHiddenBrands]);

  const visibleBrandUrlMap = useMemo(() => {
    const brandUrlMap = new Map<string, Set<string>>();

    Array.from(processed.urlBrandMap, ([url, obj]) => {
      const hiddenBrands = activeHiddenBrands.get(url);

      Array.from(obj?.keys())
        .filter((brandName) => !hiddenBrands?.has(brandName))
        .forEach((brandName) => {
          if (!brandUrlMap.has(brandName)) {
            brandUrlMap.set(brandName, new Set());
          }
          brandUrlMap.get(brandName).add(url);
        });
    });

    return new Map(Array.from(brandUrlMap, ([brand, urls]) => [brand, Array.from(urls)]));
  }, [processed.urlBrandMap, activeHiddenBrands]);

  const toggleBrandVisibility = (url: string, brandName: string) => {
    setUserOverrides((prev) => {
      const newMap = new Map(prev);
      const urlOverrides = new Set(prev.get(url) || []);

      if (urlOverrides.has(brandName)) {
        urlOverrides.delete(brandName);
      } else {
        urlOverrides.add(brandName);
      }

      newMap.set(url, urlOverrides);
      return newMap;
    });
  };

  const getImagesByUrl = (url: string, imageUrls?: string[]) => {
    if (processed.urlToImageMap.size > 0) {
      const hiddenBrands = activeHiddenBrands.get(url);
      const images = processed.urlToImageMap.get(url);
      if (!images) return [];
      else {
        return imageUrls?.length > 0
          ? images.filter((img) => imageUrls.includes(img?.imageUrl))
          : images.filter(
            (img) =>
              !img?.brandNames?.length ||
              img?.brandNames?.some((brand) => !hiddenBrands?.has(brand))
          );
      }
    }
    return [];
  };

  const handleSave = () => {
    // TODO: add logic for updating Brand Detect service
    const requests: Request[] = [];
    if (processed.dirtyServiceMap.get(SiService.CATEGORIZE_SERVICE)) {
      requests.push({
        selector: SiService.CATEGORIZE_SERVICE,
        paths: [`detail[${categorizeBrandsDetailIndex}]`, 'result.brands'],
      });
    }
    if (requests.length > 0) {
      onSaveMultiple?.(requests);
    }
    userOverrides.clear();
  };

  const handleOpenAddModal = () => {
    if (isDirty) {
      alert('You have unsaved changes. Please save before proceeding.');
    } else {
      setShowAddBrandsModal(true);
    }
  };

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          isEditable && (
            <div
              className='btn btn-icon btn-sm btn-primary'
              onClick={handleOpenAddModal}
              title='Add Brands'
            >
              <i className='fas fa-plus'></i>
            </div>
          )
        }
        isEditable={isEditable}
        isDirty={isDirty}
        onSave={handleSave}
        onReset={() => {
          // TODO:
          // onReset?.(SiService.CATEGORIZE_SERVICE)
          // setUserOverrides(new Map())
        }}
      >
        {isEditable && categoryResultsAvailable && (
          <div className='bg-light py-4 px-9 d-flex flex-column gap-4 border-start border-end'>
            <div className='d-flex flex-column gap-1 w-100'>
              <div className='fw-bolder fs-7'>Active Brands</div>
              <div className='row row-cols-1 row-cols-md-2 row-cols-xl-4 row-cols-xxl-5 g-2'>
                {/* TODO: add Brand Detect service's brands once available */}
                {data?.[SiService.CATEGORIZE_SERVICE]?.result?.brands?.map((item) => (
                  <div key={item?.brand}>
                    <ToggleLabelCard
                      label={item?.brand}
                      isHidden={item?.field_status !== FieldStatus.ACTIVE}
                      onToggle={() => {
                        onUpdate(SiService.CATEGORIZE_SERVICE, (data) => {
                          const fieldStatus =
                            item?.field_status !== FieldStatus.ACTIVE
                              ? FieldStatus.ACTIVE
                              : FieldStatus.INACTIVE;

                          data.result.brands.find(
                            (b: any) => b.brand === item?.brand
                          ).field_status = fieldStatus;
                          data.detail[categorizeBrandsDetailIndex].section_detail.forEach(
                            (urlObj, uIndex) => {
                              urlObj?.brands?.forEach((brandObj, bIndex, brandsArr) => {
                                if (brandObj?.name === item?.brand) {
                                  brandsArr[bIndex].field_status = fieldStatus;
                                }
                              });
                            }
                          );
                        });
                      }}
                      showToggle={isEditable}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className='p-9 vstack gap-4'>
          {processed.urlBrandMap.size > 0 && hasVisibleUrlBrandMap ? (
            shouldRenderSiAiReadonlyStyle ? (
              (() => {
                const visibleBrands = Array.from(visibleBrandUrlMap.keys());

                return visibleBrands.length > 0 ? (
                  <div className='d-flex flex-wrap gap-3'>
                    {visibleBrands.map((brandName, index) => (
                      (() => {
                        const brandUrls = visibleBrandUrlMap.get(brandName) ?? [];
                        const hasLinks = brandUrls.length > 0;

                        return (
                          <button
                            key={`${brandName}-${index}`}
                            type='button'
                            className='btn btn-sm btn-none d-flex align-items-center border rounded px-4 py-2'
                            onClick={() => {
                              setSelectedBrandLinks({
                                brand: brandName,
                                urls: brandUrls,
                              });
                              setShowBrandLinksModal(true);
                            }}
                          >
                            {hasLinks && (
                              <i
                                className='bi bi-link-45deg fs-5 text-muted me-1'
                                aria-hidden='true'
                              ></i>
                            )}
                            <span className='fw-bold fs-7' style={{ color: '#fd397a' }} title={brandName}>
                              {brandName}
                            </span>
                          </button>
                        );
                      })()
                    ))}
                  </div>
                ) : (
                  <div className='text-center'>{emDash}</div>
                );
              })()
            ) : (
              <>
                {Array.from(processed.urlBrandMap, ([url, obj], index) => {
                  const images = getImagesByUrl(url);
                  const areAllBrandsHidden = Array.from(obj.values()).every((brandObj) => brandObj.isHidden);
                  const hiddenBrands = activeHiddenBrands.get(url);
                  const canEditUrl =
                    isSiAiNewData &&
                    !!data?.[SiService.CATEGORIZE_SERVICE]?.detail?.[
                      categorizeBrandsDetailIndex
                    ]?.section_detail?.some((item: any) => item?.url === url);
                  const isEditingUrl = canEditUrl && editingUrlKey === url;
                  const visibleBrandNames = Array.from(obj?.keys()).filter(
                    (brandName) => !hiddenBrands?.has(brandName)
                  );
                  const visibleBrandText = visibleBrandNames.join(', ');
                  const header = isEditable ? (
                    <UrlLabelThumbnailCard
                      key={index}
                      url={url}
                      label={Array.from(obj?.keys(), (brandName, idx) =>
                        isEditable ? (
                          <span key={idx}>
                            <span
                              className={clsx(
                                hiddenBrands?.has(brandName) &&
                                'text-decoration-line-through text-muted'
                              )}
                            >
                              {brandName}
                            </span>
                            {idx < obj.size - 1 && ', '}
                          </span>
                        ) : hiddenBrands?.has(brandName) ? (
                          ''
                        ) : (
                          <span key={idx}>
                            {brandName}
                            {idx < obj.size - 1 && ', '}
                          </span>
                        )
                      )}
                      thumbnail={images?.[0]?.filePath}
                      onClickThumbnail={() => {
                        setSelectedBrandData({
                          brand: '',
                          images: images,
                        });
                        setShowImagesModal(true);
                      }}
                      className={{ container: 'p-3' }}
                    />
                  ) : (
                    <div key={index} className='d-flex justify-content-between align-items-center gap-4 w-100 border rounded p-3'>
                      <div className='flex-grow-1 min-w-0 pe-2'>
                        <UrlLabelThumbnailCard
                          url={url}
                          thumbnail={images?.[0]?.filePath}
                          onClickThumbnail={() => {
                            setSelectedBrandData({
                              brand: '',
                              images: images,
                            });
                            setShowImagesModal(true);
                          }}
                        />
                      </div>
                      <div
                        className='text-end fw-bold flex-shrink-1'
                        style={{ color: '#fd397a', maxWidth: '40%' }}
                        title={visibleBrandText}
                      >
                        <span className='d-inline-block text-truncate w-100'>
                          {visibleBrandText || emDash}
                        </span>
                      </div>
                    </div>
                  );

                  if (areAllBrandsHidden) {
                    return null;
                  }

                  return isEditable ? (
                    <SingleAccordion
                      key={index}
                      header={
                        isEditingUrl ? (
                          <div
                            className='w-100 px-3 py-4'
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <input
                              className='form-control form-control-sm'
                              value={editingUrlValue}
                              onChange={(e) => setEditingUrlValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder='Enter URL'
                            />
                          </div>
                        ) : (
                          header
                        )
                      }
                      headerActions={
                        canEditUrl
                          ? isEditingUrl
                            ? (
                              <div className='d-flex align-items-center gap-2'>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-success'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const trimmedUrl = editingUrlValue.trim();
                                    if (!trimmedUrl) {
                                      return;
                                    }

                                    onUpdate(SiService.CATEGORIZE_SERVICE, (draft) => {
                                      const sectionDetails =
                                        draft?.detail?.[categorizeBrandsDetailIndex]?.section_detail ??
                                        [];
                                      const brandDetail = sectionDetails.find(
                                        (detailItem: any) => detailItem?.url === url
                                      );
                                      if (brandDetail) {
                                        brandDetail.url = trimmedUrl;
                                      }
                                    });

                                    setUserOverrides((prev) => {
                                      const next = new Map(prev);
                                      if (next.has(url)) {
                                        next.set(trimmedUrl, next.get(url));
                                        next.delete(url);
                                      }
                                      return next;
                                    });

                                    setEditingUrlKey(null);
                                    setEditingUrlValue('');
                                  }}
                                  disabled={!editingUrlValue.trim()}
                                >
                                  Save
                                </button>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingUrlKey(null);
                                    setEditingUrlValue('');
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type='button'
                                className='btn btn-sm btn-light-primary'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUrlKey(url);
                                  setEditingUrlValue(url ?? '');
                                }}
                              >
                                <i className='bi bi-pencil-square'></i>
                              </button>
                            )
                          : undefined
                      }
                      open
                      className={{ body: 'border-top py-0 pb-3' }}
                    >
                      <BrandTable
                        data={Array.from(obj, ([brandName, brandObj]) => ({
                          name: brandName,
                          sector: brandObj?.sector,
                          snippets: brandObj?.snippets ?? [],
                          images:
                            brandObj?.imageUrls?.length > 0
                              ? getImagesByUrl(url, brandObj.imageUrls)
                              : [],
                        }))}
                        hiddenBrands={hiddenBrands}
                        onToggleVisibility={(brand, sector) => {
                          if (sector) {
                            // categorize service
                            onUpdate(SiService.CATEGORIZE_SERVICE, (data) => {
                              const categorizeBrandDetail =
                                data.detail[categorizeBrandsDetailIndex].section_detail;
                              const brandDetail = categorizeBrandDetail
                                .find((u) => u.url === url)
                                .brands.find((b) => b.name === brand);
                              brandDetail.field_status =
                                brandDetail.field_status === FieldStatus.ACTIVE
                                  ? FieldStatus.INACTIVE
                                  : FieldStatus.ACTIVE;

                              const isActiveBrand = categorizeBrandDetail.some((u: any) =>
                                u.brands.some(
                                  (b: any) =>
                                    b.name === brand && b.field_status === FieldStatus.ACTIVE
                                )
                              );
                              data.result.brands.find((b: any) => b.brand === brand).field_status =
                                isActiveBrand ? FieldStatus.ACTIVE : FieldStatus.INACTIVE;
                            });
                          } else {
                            // brand detect service
                            toggleBrandVisibility(url, brand);
                          }
                        }}
                        onExpandImages={(brand, images) => {
                          setSelectedBrandData({
                            brand: brand,
                            images: images,
                          });
                          setShowImagesModal(true);
                        }}
                      />
                    </SingleAccordion>
                  ) : (
                    header
                  );
                })}
              </>
            )
          ) : (
            <div className='text-center'>{t('NO_BRAND_FOUND')}</div>
          )}
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      <ImageListModal
        title={
          selectedBrandData?.brand
            ? t('CAPTURES_FOR_BRAND', { brand: selectedBrandData?.brand })
            : t('CAPTURES')
        }
        imagePaths={selectedBrandData?.images?.map((image) => image?.filePath) ?? []}
        show={showImagesModal}
        onHide={() => setShowImagesModal(false)}
        renderSelectedImageOverlayContent={(selectedImageIndex) =>
          selectedBrandData?.images?.[selectedImageIndex]?.imageUrl && (
            <a
              href={selectedBrandData?.images?.[selectedImageIndex]?.imageUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='btn btn-sm btn-primary btn-shadow'
            >
              Visit
            </a>
          )
        }
        restoreFocus={false} // prevent restoring focus to previously focused element on hide
      />

      <AddBrandsModal
        data={data}
        show={showAddBrandsModal}
        isSiAiNewData={isSiAiNewData}
        handleClose={() => {
          setShowAddBrandsModal(false);
        }}
        handleUpdate={onUpdate}
        handleSaveMultiple={onSaveMultiple}
        handleRefetchImages={refetchImages}
      />

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?.[SiService.CATEGORIZE_SERVICE]?._id,
            paths: ['result.brands'],
          },
        ]}
      />

      <SiAiLinksModal
        show={showBrandLinksModal}
        onClose={() => setShowBrandLinksModal(false)}
        title={t('REFERENCE_URLS')}
        links={selectedBrandLinks?.urls}
      />
    </>
  );
}
